import React, { useRef, useState } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { format, parseISO } from 'date-fns';

import { useRatesSeries } from '../api/treasuryApi';
import { cn, formatDate, formatYield } from '../utils/formatters';
import { ChartDownloadMenu } from '../../../../shared/ui';
import { useChartExport } from '../../../../shared/hooks/useChartExport';
import { buildChartFilename } from '../../../../shared/utils/chartFilename';

// 1. CONFIGURATION: We define a beautiful color palette and labels for every possible tenor.
// We use a progressive color scale from cool (short-term) to warm (long-term).
const TENOR_CONFIG = [
    { key: 'month1', label: '1M', color: '#94a3b8' }, // slate-400
    { key: 'month3', label: '3M', color: '#64748b' }, // slate-500
    { key: 'month6', label: '6M', color: '#0ea5e9' }, // sky-500
    { key: 'year1',  label: '1Y', color: '#3b82f6' }, // blue-500
    { key: 'year2',  label: '2Y', color: '#6366f1' }, // indigo-500
    { key: 'year5',  label: '5Y', color: '#8b5cf6' }, // violet-500
    { key: 'year10', label: '10Y', color: '#ec4899' },// pink-500
    { key: 'year30', label: '30Y', color: '#f43f5e' },// rose-500
];

const TIME_WINDOWS = [
    { label: '1Y', value: 1 },
    { label: '3Y', value: 3 },
    { label: '5Y', value: 5 },
    { label: '10Y', value: 10 },
];

export const RatesSeriesChart: React.FC = () => {
    // 2. STATE MANAGEMENT: The UI controls directly drive what the API fetches.
    // We default to a classic institutional view: 2Y vs 10Y over 3 years.
    const [years, setYears] = useState<number>(3);
    const [selectedTenors, setSelectedTenors] = useState<string[]>(['year2', 'year10']);

    const containerRef = useRef<HTMLDivElement>(null);
    const { downloadPng, downloadJpeg } = useChartExport(containerRef);

    // 3. DATA FETCHING: Whenever state changes, React Query smartly fetches ONLY the needed columns.
    // Notice how we join the array into a comma-separated string!
    const { data, isLoading, error } = useRatesSeries(
        years,
        selectedTenors.length > 0 ? selectedTenors.join(',') : undefined,
        true // includeRegime = true for our custom tooltip!
    );

    // Toggle handler for the tenor chips
    const toggleTenor = (key: string) => {
        setSelectedTenors((prev) => {
            if (prev.includes(key)) {
                // Prevent removing the very last line (keep at least one visible)
                if (prev.length === 1) return prev;
                return prev.filter((t) => t !== key);
            }
            // Add and sort by config order so legends/tooltips stay organized visually
            const next = [...prev, key];
            return next.sort(
                (a, b) =>
                    TENOR_CONFIG.findIndex((t) => t.key === a) -
                    TENOR_CONFIG.findIndex((t) => t.key === b)
            );
        });
    };

    // 4. CUSTOM TOOLTIP: A highly polished tooltip that shows all hovered lines + the Market Regime.
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload || !payload.length) return null;

        // Extract the market regime from the payload (included because includeRegime=true)
        const regime = payload[0]?.payload?.market_regime;

        return (
            <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm p-4 rounded-lg shadow-xl border border-slate-200 dark:border-slate-800 z-50 min-w-[200px]">
                <div className="flex justify-between items-center mb-3 pb-2 border-b border-slate-100 dark:border-slate-800">
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300">
                        {formatDate(label)}
                    </p>
                    {regime && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 ml-4">
                            {regime}
                        </span>
                    )}
                </div>

                <div className="space-y-1.5">
                    {/* Sort payload to show highest yields at the top for easy reading */}
                    {[...payload]
                        .sort((a, b) => (b.value || 0) - (a.value || 0))
                        .map((entry: any, index: number) => {
                            const config = TENOR_CONFIG.find((tc) => tc.key === entry.dataKey);
                            return (
                                <div key={`item-${index}`} className="flex items-center justify-between gap-6">
                                    <span className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                                        <span
                                            className="w-2.5 h-2.5 rounded-full"
                                            style={{ backgroundColor: entry.color }}
                                        />
                                        {config?.label || entry.dataKey}
                                    </span>
                                    <span className="font-mono font-bold text-slate-900 dark:text-slate-100">
                                        {formatYield(entry.value)}
                                    </span>
                                </div>
                            );
                        })}
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 flex flex-col h-full min-h-[450px]">

            {/* Header & Controls */}
            <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        Historical Yield Comparison
                        {isLoading && (
                            <span className="flex h-2 w-2 relative ml-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                            </span>
                        )}
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Select tenors below to compare rates across maturities dynamically.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    {/* Time Window Selectors */}
                    <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                        {TIME_WINDOWS.map((tw) => (
                            <button
                                key={tw.value}
                                onClick={() => setYears(tw.value)}
                                className={cn(
                                    "px-3 py-1.5 text-xs font-bold rounded-md transition-colors",
                                    years === tw.value
                                        ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                                        : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                )}
                            >
                                {tw.label}
                            </button>
                        ))}
                    </div>
                    <ChartDownloadMenu
                        label="Historical Yield Comparison chart"
                        onPng={() => downloadPng({ filename: buildChartFilename('yield-series', [`${years}Y`, selectedTenors.join('-')]) })}
                        onJpeg={() => downloadJpeg({ filename: buildChartFilename('yield-series', [`${years}Y`, selectedTenors.join('-')]) })}
                    />
                </div>
            </div>

            {/* Tenor Toggle Chips */}
            <div className="flex flex-wrap gap-2 mb-6">
                {TENOR_CONFIG.map((tenor) => {
                    const isSelected = selectedTenors.includes(tenor.key);
                    return (
                        <button
                            key={tenor.key}
                            onClick={() => toggleTenor(tenor.key)}
                            className={cn(
                                "px-3 py-1 text-xs font-semibold rounded-full border transition-all duration-200 flex items-center gap-2",
                                isSelected
                                    ? "bg-slate-800 text-white border-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:border-slate-100 shadow-sm"
                                    : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 dark:bg-slate-900 dark:border-slate-700 dark:hover:border-slate-600"
                            )}
                        >
                            <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: tenor.color, opacity: isSelected ? 1 : 0.5 }}
                            />
                            {tenor.label}
                        </button>
                    );
                })}
            </div>

            {/* Chart Area */}
            <div ref={containerRef} className="flex-1 relative min-h-[250px]">
                {error && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center text-rose-500 bg-rose-50/50 dark:bg-rose-900/10 rounded-lg text-sm">
                        Failed to load chart data.
                    </div>
                )}

                {data && data.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#cbd5e1" opacity={0.2} />
                            <XAxis
                                dataKey="date"
                                tickFormatter={(tick) => {
                                    try {
                                        return format(parseISO(tick), "MMM ''yy"); // e.g. "Jan '23"
                                    } catch { return tick; }
                                }}
                                tick={{ fontSize: 12, fill: '#64748b' }}
                                minTickGap={50}
                                axisLine={false}
                                tickLine={false}
                                dy={10}
                            />
                            <YAxis
                                domain={['auto', 'auto']}
                                tickFormatter={(tick) => `${tick.toFixed(1)}%`}
                                tick={{ fontSize: 12, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                dx={-10}
                            />
                            <Tooltip
                                content={<CustomTooltip />}
                                cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '4 4' }}
                            />

                            {/* 5. DYNAMIC LINE RENDERING */}
                            {selectedTenors.map((key) => {
                                const config = TENOR_CONFIG.find((tc) => tc.key === key);
                                return (
                                    <Line
                                        key={key}
                                        type="monotone"
                                        dataKey={key}
                                        stroke={config?.color}
                                        strokeWidth={2.5}
                                        dot={false}
                                        activeDot={{ r: 6, strokeWidth: 0, fill: config?.color }}
                                        // CRITICAL: connectNulls bridges the gap if extremely old FMP data lacks a tenor on a specific day
                                        connectNulls={true}
                                    />
                                );
                            })}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    !isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm italic">
                            Select at least one tenor above to view data.
                        </div>
                    )
                )}
            </div>
        </div>
    );
};
